"use client"

import * as React from "react"
import { Link } from "react-router-dom"
import { MoreHorizontalIcon, type LucideIcon } from "lucide-react"

import { useTranslations } from "@/i18n"
import { useIsMobile } from "@/shared/hooks/use-mobile"
import {
  Button,
  cn,
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@ibada/ui"

export type RowActionsMenuItem = {
  id: string
  label: string
  icon: LucideIcon
  /** What the item does; or give `href` for an item that opens a page (a real link, so it can open in a tab). */
  onSelect?: () => void
  href?: string
  destructive?: boolean
  disabled?: boolean
}

type RowActionsMenuProps = {
  /** Items in groups: a separator (desktop) or a separate card (phone) sits between groups. */
  groups: RowActionsMenuItem[][]
  label: string
  openLabel: string
  /** The record itself, heading the phone sheet. */
  sheetHeader: React.ReactNode
}

/**
 * A row's ⋯ actions. Desktop keeps the dropdown; a phone opens the app's bottom sheet headed by the record,
 * with each group as a rounded card and a close button at the bottom (the mobile list design).
 */
export function RowActionsMenu({ groups, label, openLabel, sheetHeader }: RowActionsMenuProps) {
  const isMobile = useIsMobile()
  const tMobile = useTranslations("common.dataTableMobile")
  const [sheetOpen, setSheetOpen] = React.useState(false)
  const visibleGroups = groups.filter((group) => group.length > 0)

  if (isMobile) {
    return (
      <Drawer open={sheetOpen} onOpenChange={setSheetOpen}>
        <DrawerTrigger asChild>
          <Button variant='ghost' size='icon' className='size-11 rounded-full text-muted-foreground'>
            <span className='sr-only'>{openLabel}</span>
            <MoreHorizontalIcon />
          </Button>
        </DrawerTrigger>
        <DrawerContent>
          <DrawerHeader className='group-data-[vaul-drawer-direction=bottom]/drawer-content:text-left'>
            <DrawerTitle className='sr-only'>{label}</DrawerTitle>
            <DrawerDescription asChild>
              <div>{sheetHeader}</div>
            </DrawerDescription>
          </DrawerHeader>
          <div className='flex flex-col gap-2.5 px-3 pb-[max(1rem,env(safe-area-inset-bottom))]'>
            {visibleGroups.map((group) => (
              <ul key={group[0].id} className='divide-y divide-border/60 overflow-hidden rounded-2xl bg-muted/60'>
                {group.map((item) => (
                  <li key={item.id}>
                    {item.href ? (
                      <Link
                        to={item.href}
                        onClick={() => setSheetOpen(false)}
                        className={cn(
                          "flex min-h-12 w-full items-center gap-3 px-4 text-left text-[15px] font-semibold active:bg-muted",
                          item.destructive ? "text-destructive" : "text-foreground",
                        )}
                      >
                        <item.icon
                          aria-hidden
                          className={cn("size-[1.125rem]", item.destructive ? "text-destructive" : "text-muted-foreground")}
                        />
                        {item.label}
                      </Link>
                    ) : (
                      <button
                        type='button'
                        disabled={item.disabled}
                        onClick={() => {
                          setSheetOpen(false)
                          item.onSelect?.()
                        }}
                        className={cn(
                          "flex min-h-12 w-full items-center gap-3 px-4 text-left text-[15px] font-semibold active:bg-muted disabled:opacity-50",
                          item.destructive ? "text-destructive" : "text-foreground",
                        )}
                      >
                        <item.icon
                          aria-hidden
                          className={cn("size-[1.125rem]", item.destructive ? "text-destructive" : "text-muted-foreground")}
                        />
                        {item.label}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            ))}
            <DrawerClose asChild>
              <button type='button' className='h-12 rounded-full bg-muted/60 text-[15px] font-extrabold text-foreground active:bg-muted'>
                {tMobile("closeSheet")}
              </button>
            </DrawerClose>
          </div>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <div className='text-right'>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant='ghost' size='icon-sm'>
            <span className='sr-only'>{openLabel}</span>
            <MoreHorizontalIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end'>
          <DropdownMenuLabel>{label}</DropdownMenuLabel>
          {visibleGroups.map((group, index) => (
            <React.Fragment key={group[0].id}>
              {index > 0 ? <DropdownMenuSeparator /> : null}
              {group.map((item) => (
                item.href ? (
                  <DropdownMenuItem
                    key={item.id}
                    asChild
                    disabled={item.disabled}
                    variant={item.destructive ? "destructive" : undefined}
                  >
                    <Link to={item.href}>{item.label}</Link>
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    key={item.id}
                    disabled={item.disabled}
                    variant={item.destructive ? "destructive" : undefined}
                    onSelect={item.onSelect}
                  >
                    {item.label}
                  </DropdownMenuItem>
                )
              ))}
            </React.Fragment>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
